import React, { useState, useEffect, useContext } from "react";
import { Row, Col, Button, Form, Table, OverlayTrigger, Popover, Tooltip } from "react-bootstrap";
import { Link } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import CustomFormCheck from "utils/CustomFormCheck";
import jsDig from "utils/jsDig";
import BackButton from "../BackButton";
import NextButton from "../NextButton";
import SkipButton from "../SkipButton";
import getNextPage from "../getNextPage";
import { RouterContext } from "../Onboarding";
import CustomTooltip from "utils/CustomTooltip";

export default function GoogleAnalyticsAccess(props) {
  const route = useContext(RouterContext);

  const nextPage = getNextPage(props.onboarding_progress);

  let segmentImg = require("images/analytics-segment.png");
  segmentImg = <img src={`/datapulse${segmentImg}`} />;
  const tooltipMessage = <div>In Analytics, apply the segment you wish to use and check the URL for the parameter '_.useg'. The segment ID is everything that follows the 'user' string.  For pulling a built-in segment, take the number after 'builtin' and add a hyphen '-' before.<br></br> {segmentImg}</div>

  useEffect(() => {
    window.localStorage.setItem("routerHistory", JSON.stringify([...route.get]));
    props.googleAnalyticsIds.map((googleAnalyticsId, index) => {
      handleIdChange(index, "custom_segment", "");
    });
  }, [route.get]);
  const [userGrantAccess, setUserGrantAccess] = useState(
    props.teamAccess["Google Analytics"]["Full"] == "1" || props.teamAccess["Google Analytics"]["Restricted"] == "1"
  );
  const handleIdDelete = (targetIndex) => {
    let newIds = props.googleAnalyticsIds.filter((id) => {
      return targetIndex != id.frontId;
    });
    props.setGoogleAnalyticsIds(newIds);
  };

  const handleIdChange = (targetIndex, property, value) => {
    let newIds = props.googleAnalyticsIds.map((id, index) => {
      let newId = id;
      if (index == targetIndex) {
        newId[property] = value;
      }
      return newId;
    });
    props.setGoogleAnalyticsIds(newIds);
  };

  props.pageLocator();

  const [validated, setValidated] = useState(false);

  useEffect(() => {
    const form = document.getElementById("onboarding-form");
    if ((form.checkValidity() === true) && (props.googleAnalyticsIds.length > 0) && ((props.teamAccess["Google Analytics"]["Full"] == "1") || props.teamAccess["Google Analytics"]["Restricted"] == "1")) {
      setValidated(true);
    } else {
      setValidated(false);
    }
  });

  const authenticateFunction = (platform) => {
    window.open(platform["auth_url"], '_blank');
  };

  const deauthorizeFunction = (platform) => {
    window.open(platform["deauth_url"], '_blank');
  };

  const resetMessage = (platformName) => {
    return (`Resetting ${capitalize(platformName)} token will require you to authenticate again`)
  }

  const authButton = (platform) => {
    if (platform.authenticated) {
      return (
        <>
          <Button className="mb-4 btn-lg text-center" variant="danger" onClick={() => deauthorizeFunction(platform)}>
            Reset Token
            <CustomTooltip faClass="text-white far fa-question-circle f18 pl-2" message={resetMessage(platform["title"])} inline={true} />
          </Button>
        </>
      )
    } else {
      return (
        <>
          <Button className="mb-4 btn-lg text-center" variant="success" onClick={() => authenticateFunction(platform)}>
            Authenticate
            <i className="fas fa-cog pl-2" style={{ paddingRight: "0px", color: "unset", float: "unset" }}></i>
          </Button>
        </>
      )
    }
  }

  let firstImg = require("images/analytics-screenshot-1.png");
  let secondImg = require("images/analytics-screenshot-2.png");
  firstImg = <img style={{ maxWidth: '400px' }} src={`/datapulse${firstImg}`} />;
  secondImg = <img style={{ maxWidth: '400px' }} src={`/datapulse${secondImg}`} />;
  const idTooltipMessage = () => {
    return (
      <>
        <div>
          There are 2 types of Google Analytics properties, GA4 and UA.
          <br></br>
          <br></br>
          For UA, the ID to use in DataPulse is the view ID. That can be extracted from the URL after you select the relevant view:
          {firstImg}
          <br></br>
          The view ID is the number that comes after the 'p' in the URL.
          <br></br>
          <br></br>
          For GA4, the ID to use is the profile ID. That can also be extracted from the URL after you select the relevant property:
          {secondImg}
          <br></br>
          The property ID is the number that comes after the 'p' in the URL.
        </div>
      </>
    )
  }

  return (
    <>
      <Row className=" px-4 mb-3 pb-3 justify-content-center">
        <Col md={7}>
          <div>
            <p className="pb-3 f28 visby-bold primary text-center">Grant Google Analytics Access</p>
          </div>
          <div>
            <p className=" f14 nunito-regular primary ">
              On this page you will grant access to Google Analytics. Provide your Account ID's below, and use the 'Label' fields to name elements for your own
              reference (this will not affect data in any way). You can then check the access level that you have granted your team. It's important to note that
              checking the access level boxes are solely for reference, and will not perform any actions in regards to granting access to your account.
            </p>
          </div>
        </Col>
      </Row>
      <Row className="mb-5 justify-content-around GA-access text-left">
        <Col style={{ maxWidth: "100%" }} className="mr-0" lg={6}>
          <Row>
            <Col className="mr-0" md={12}>
              <p className="visby-bold pb-2 light-primary f14">Authenticate with Google:</p>
            </Col>
          </Row>
          <Row className="pb-4">
            <Col>
              <div style={{ backgroundColor: "#E1E1E1", paddingRight: "40px", paddingTop: "20px", paddingLeft: "10px" }}>
                <div className="ml-3">
                  {authButton(props.api_platforms["google_analytics"])}
                  <p className="f-14 light-primary pb-3">{props.api_platforms["google_analytics"]["authenticated"] ?
                    "You have already authenticated with Google Analytics. If you need access to clients on a different account, reset your token and reauthenticate."
                    : "You must go through the authentication process before you can pull data from Google Analytics!"
                  }
                  </p>
                </div>
              </div>
            </Col>
          </Row>
          <Row>
            <Col className="mr-0 pr-5" md={12}>
              <p className="visby-bold pb-2 light-primary f14">Provide your account information below:</p>
            </Col>
          </Row>
          <Row>
            <Col>
              <div style={{ backgroundColor: "#E1E1E1", padding: "15px", backgroundColor: "#E1E1E1" }}>
                <div className="table-responsive">
                  <Table style={{ minWidth: "500px" }}>
                    <thead>
                      <tr>
                        <td style={{ width: "20%", maxWidth: "20%" }} className="pl-3 f14 light-primary">
                          <span className="f12 light-primary pr-1">Label</span>
                          <CustomTooltip
                            delay={1700}
                            faClass=" far fa-question-circle"
                            inline
                            message={<p>Label is mainly for handling multiple google analytics ids, if you do not know what to put, you can just put main</p>}
                          />
                        </td>
                        <td style={{ width: "20%", maxWidth: "20%" }} className="pl-3 f12 light-primary">
                          <span>ID</span>
                          <span>
                            <CustomTooltip
                              faClass="far  fa-question-circle f14 pl-2"
                              message= {idTooltipMessage()}
                              inline={true}
                            />
                          </span>
                        </td>
                        <td className="pl-3 f12 light-primary" style={{ whiteSpace: "nowrap", float: "left", width: "20%", maxWidth: "20%" }}>
                          <span className="f12 light-primary" style={{ paddingTop: "10px" }}>
                            Add custom segment ID
                          </span>
                          <span>
                            <CustomTooltip
                              faClass="far  fa-question-circle f14 pl-2"
                              message={tooltipMessage}
                              inline={true}
                            />
                          </span>
                        </td>
                        <td style={{ maxWidth: "10%", width: "20%" }}></td>
                      </tr>
                    </thead>
                    <tbody>
                      {props.googleAnalyticsIds.map((googleAnalyticsId, index) => {
                        return (
                          <tr key={index}>
                            <td>
                              <Form.Control
                                size="sm"
                                placeholder="Label"
                                style={{ width: "162px" }}
                                onChange={(e) => handleIdChange(index, "label", e.target.value)}
                                value={jsDig(["label"], googleAnalyticsId) ? jsDig(["label"], googleAnalyticsId) : ""}
                                id={`ob-analytics-label${index}`}
                              />
                            </td>
                            <td>
                              <Form.Control
                                size="sm"
                                placeholder="ID"
                                style={{ width: "145px" }}
                                value={jsDig(["id"], googleAnalyticsId) ? jsDig(["id"], googleAnalyticsId) : ""}
                                onChange={(e) => handleIdChange(index, "id", e.target.value)}
                                id={`ob-analytics-id${index}`}
                                required
                                type="text"
                              />
                            </td>
                            <td style={{ width: "180px", whiteSpace: "nowrap" }} className="pl-3">
                              <div style={{ display: "inline-block", maxWidth: "20%" }}>
                                <CustomFormCheck
                                  onChange={(e) => handleIdChange(index, "custom_segment_enabled", e.target.checked ? "1" : "0")}
                                  sendUncheckedValue
                                  id={`ob-google-analytics-id-${index}-custom-segment-enabled`}
                                  checked={jsDig(["custom_segment_enabled"], googleAnalyticsId) == "1" ? true : false}
                                />
                              </div>
                              <Form.Control
                                className = "analytics-segment-id-onboarding"
                                required={props.googleAnalyticsIds[index]["custom_segment_enabled"] === "1" ? true : false}
                                size="sm"
                                placeholder="Custom segment ID"
                                value={jsDig(["custom_segment"], googleAnalyticsId) ? jsDig(["custom_segment"], googleAnalyticsId) : ""}
                                onChange={(e) => handleIdChange(index, "custom_segment", e.target.value)}
                                style={{ display: "inline-block", width: "80%" }}
                              />

                            </td>
                            <td>
                              <i onClick={() => handleIdDelete(googleAnalyticsId.frontId)} className=" pl-2 fas fa-minus-circle"></i>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
                <Button
                  className="mt-1 mb-1 ml-2 mr-4 px-4 btn-sm "
                  size="sm"
                  as="input"
                  type="button"
                  onClick={(e) => {
                    props.setGoogleAnalyticsIds((prevState) => [...prevState, { frontId: uuidv4() }]);
                  }}
                  value="+ Add Google Analytics ID"
                  variant="success"
                />
                <input hidden name="current_onboarding_page" value={window.location.href} readOnly />
              </div>
            </Col>
          </Row>
          {/* <Row className="my-3">
            <Col className="">
              <p className="f14 pb-2 mt-0 visby-bold light-primary">Email to grant access to:</p>
              <p className="f16 primary">support@digitalposition.com</p>
            </Col>
          </Row> */}
          <Row>
            <Col md={10}>
              <div style={{ marginTop: "14px" }}>
                <p className="f14 primary">Complete as many of the below steps as possible, and check to confirm.</p>
              </div>
              <div className="grey-box text-left" style={{ backgroundColor: "#E1E1E1", marginTop: "14px" }}>
                <div className="pb-1">
                  <CustomFormCheck
                    required
                    className="primary"
                    id="ob-google-analytics-provided"
                    name="google-analytics-provided"
                    label="Access has been granted by client"
                    checked={userGrantAccess}
                    onChange={(e) => setUserGrantAccess(!userGrantAccess)}
                  />
                </div>
                <div>
                  <p className="pl-3 pb-1 f16 primary">Please indicate access granted.</p>
                </div>

                <div className="pb-1 pl-3">
                  <CustomFormCheck
                    id="ob-google-analytics-full"
                    name="google-analytics-full"
                    label="Full"
                    disabled={!userGrantAccess}
                    checked={props.teamAccess["Google Analytics"]["Full"] == "1"}
                    onChange={(e) => {
                      let accessChecked = props.teamAccess;
                      accessChecked["Google Analytics"]["Restricted"] = "0";
                      accessChecked["Google Analytics"]["Full"] = props.teamAccess["Google Analytics"]["Full"] == "1" ? "0" : "1";
                      props.setTeamAccess({ ...accessChecked });
                    }}
                  />
                </div>
                <div className="pb-1 pl-3">
                  <CustomFormCheck
                    id="ob-google-analytics-restricted"
                    name="google-analytics-restricted"
                    label="Restricted"
                    disabled={!userGrantAccess}
                    checked={props.teamAccess["Google Analytics"]["Restricted"] == "1"}
                    onChange={(e) => {
                      let accessChecked = props.teamAccess;
                      accessChecked["Google Analytics"]["Full"] = "0";
                      accessChecked["Google Analytics"]["Restricted"] = props.teamAccess["Google Analytics"]["Restricted"] == "1" ? "0" : "1";
                      props.setTeamAccess({ ...accessChecked });
                    }}
                  />
                </div>
              </div>
            </Col>
          </Row>
        </Col>
        {/* <Col lg={4} className="mt-3">
          <iframe
            width="560"
            height="315"
            src="https://www.youtube.com/embed/ZqFKZWPYeOQ"
            title="YouTube video player"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{ maxWidth: "100%" }}></iframe>
        </Col> */}
      </Row>
      <Row className="justify-content-center">
        <Col>
          <div className="navButtonRow">
            <BackButton />
            <SkipButton btnText={`Skip for now`} nextPage={nextPage.url} />
            <NextButton
              validated={validated}
              btnText={`Next`}
              nextPage={nextPage.url}
              invalidText={`We need more information on this page before you continue.`}
              submitForm={props.submitForm}
            />
          </div>
        </Col>
      </Row>
    </>
  );
}
